import{
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { getQueryString } from '~/utils/client-utils';
import InternalApiAxiosInstance from '~/services/requestsService/internalApiAxiosInstance';

interface IPutRequestOptions {
  qs?: any,
  headers?: any,
}

interface IPutRequest {
  url: string,
  requestBody?: any,
  options?: IPutRequestOptions
}

function checkStatus(response: AxiosResponse) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  const error: any = new Error(response.statusText);
  error.response = response;
  throw error;
}

function parseJSON(response: AxiosResponse) {
  return response.data;
}

function errorHandling(error: AxiosError, requestData: any, isThrow?: boolean) {
  console.error(error);
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    const { data, status, statusText } = error.response;
    console.error(new Error('Axios error:response'), {
      tags: {
        data,
        status: `${status}`,
        statusText,
      }
    });
  }

  if (error.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    console.error(new Error('Axios error:request'), {
      tags: {
        request: error.request,
      }
    });
  } else {
    console.error('Axios error:unknown', {
      tags: {
        message: error.message,
      }
    });
  }
  // it is important to not throw error for fetch to not block SSR page load.
  if (isThrow) {
    throw error;
  }
  return null;
}

async function fetch<TResponse>(url: string, options: any = {}) {
  const newOptions = options;
  newOptions.headers = {
    Accept: 'application/json',
  };
  const requestURL = `${url}${getQueryString(options.qs)}`;
  try {
    const {data} = await InternalApiAxiosInstance.get<TResponse>(requestURL, newOptions);
    return data;
  } catch (error) {
    return errorHandling(error as any, options.qs, false);
  }
}

async function put<TResponse>({ url, requestBody, options }: IPutRequest) {
  const newOptions: IPutRequestOptions = { ...options };
  newOptions.headers = {
    Accept: 'application/json',
  };
  const requestURL = `${url}${getQueryString(newOptions.qs)}`;
  try {
    const response = await InternalApiAxiosInstance.put<TResponse>(requestURL, requestBody, newOptions);
    const response_1 = checkStatus(response);
    return parseJSON(response_1);
  } catch (error) {
    return errorHandling(error as any, requestBody, true);
  }
}

async function patch<TResponse>(url: string, requestBody: any, options?: IPutRequestOptions) {
  const newOptions: IPutRequestOptions = { ...options };
  newOptions.headers = {
    Accept: 'application/json',
  };
  const requestURL = `${url}${getQueryString(newOptions.qs)}`;
  try {
    const response = await InternalApiAxiosInstance.patch<TResponse>(requestURL, requestBody, newOptions);
    const response_1 = checkStatus(response);
    return parseJSON(response_1);
  } catch (error) {
    return errorHandling(error as any, requestBody, true);
  }
}

function post<TResponse>(url: string, requestBody?: any, options?: AxiosRequestConfig) {
  const newOptions = options ?? {};
  const jsonHeader = {
    Accept: 'application/json',
  };
  if (newOptions?.headers) {
    newOptions.headers = {
      ...jsonHeader,
      ...newOptions.headers,
    };
  } else {
    newOptions.headers = jsonHeader;
  }
  const requestURL = `${url}${getQueryString(newOptions.params)}`;
  return InternalApiAxiosInstance.post<TResponse>(requestURL, requestBody, newOptions)
    .then(checkStatus)
    .then(parseJSON)
    .catch((error) => errorHandling(error, requestBody, true));
}

function deleteRequest<TResponse>(url: string, options?: AxiosRequestConfig) {
  const newOptions = options ?? {};
  newOptions.headers = {
    Accept: 'application/json',
    ...newOptions.headers,
  };
  const requestURL = `${url}${getQueryString(newOptions.params)}`;
  return InternalApiAxiosInstance.delete<TResponse>(requestURL, newOptions)
    .then(checkStatus)
    .then(parseJSON)
    .catch((error) => errorHandling(error, null, true));
}

const requestService = {
  fetch,
  put,
  patch,
  post,
  delete: deleteRequest
};

export default requestService;
